import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

export const LAUNCH_SPLASH_SAFETY_TIMEOUT_MS = 10_000;
export const LAUNCH_SPLASH_RETRY_DELAY_MS = 1_000;
export const LAUNCH_SPLASH_MAX_ADDITIONAL_RETRIES = 3;

let safetyTimeout: ReturnType<typeof setTimeout> | undefined;
let retryTimeout: ReturnType<typeof setTimeout> | undefined;
let hideInFlight: Promise<void> | undefined;
let hidden = false;
let retryAfterFailure = false;
let additionalRetriesAttempted = 0;
let nativePlatformOverride: boolean | undefined;
let hideSplashOverride: (() => Promise<void>) | undefined;

export function armLaunchSplashSafetyTimeout(
  timeoutMs = LAUNCH_SPLASH_SAFETY_TIMEOUT_MS,
): void {
  if (!isNativePlatform() || hidden || safetyTimeout) return;

  safetyTimeout = setTimeout(() => {
    safetyTimeout = undefined;
    retryAfterFailure = true;
    void hideLaunchSplash('safety-timeout');
  }, timeoutMs);
}

export function hideLaunchSplash(reason: string): Promise<void> {
  if (!isNativePlatform() || hidden) {
    return Promise.resolve();
  }
  if (hideInFlight) return hideInFlight;

  hideInFlight = hideNativeSplash()
    .then(() => {
      hidden = true;
      clearSafetyTimeout();
    })
    .catch((error: unknown) => {
      console.error(`[SplashScreen] Failed to hide (${reason}).`, error);
      scheduleRetryAfterFailure();
    })
    .finally(() => {
      hideInFlight = undefined;
    });

  return hideInFlight;
}

function isNativePlatform(): boolean {
  return nativePlatformOverride ?? Capacitor.isNativePlatform();
}

function hideNativeSplash(): Promise<void> {
  return hideSplashOverride ? hideSplashOverride() : SplashScreen.hide();
}

function scheduleRetryAfterFailure(): void {
  if (
    !retryAfterFailure ||
    retryTimeout ||
    additionalRetriesAttempted >= LAUNCH_SPLASH_MAX_ADDITIONAL_RETRIES
  ) {
    return;
  }

  retryTimeout = setTimeout(() => {
    retryTimeout = undefined;
    additionalRetriesAttempted += 1;
    void hideLaunchSplash(`safety-timeout-retry-${additionalRetriesAttempted}`);
  }, LAUNCH_SPLASH_RETRY_DELAY_MS);
}

function clearSafetyTimeout(): void {
  if (safetyTimeout) {
    clearTimeout(safetyTimeout);
    safetyTimeout = undefined;
  }

  if (retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = undefined;
  }
}

export function resetLaunchSplashStateForTesting(): void {
  clearSafetyTimeout();
  hideInFlight = undefined;
  hidden = false;
  retryAfterFailure = false;
  additionalRetriesAttempted = 0;
  nativePlatformOverride = undefined;
  hideSplashOverride = undefined;
}

export function setLaunchSplashNativePlatformForTesting(isNative: boolean): void {
  nativePlatformOverride = isNative;
}

export function setLaunchSplashHideForTesting(hide: () => Promise<void>): void {
  hideSplashOverride = hide;
}
